import { Button, Flex, Menu, MenuButton, MenuItem, MenuList } from "@chakra-ui/react";
import { faChevronDown } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    isBefore,
    isValid
} from 'date-fns';
import { BaseDatepicker } from "../components/Datepicker";
import type { UseTimeRangeReturn } from "../hooks";
import { generateHistoricalPeriods, getHistoricalLabel, HISTORICAL_COUNTS, PERIOD_TYPES } from "../hooks";

type DetailedTimeRangeSelectorProps = UseTimeRangeReturn;

export const TimeRangeSelector = ({
    periodType,
    setPeriodType,
    historicalPeriod,
    setHistoricalPeriod,
    customRange,
    setCustomRange
}: DetailedTimeRangeSelectorProps) => {

    // Handle custom date changes with validation
    const handleCustomDateChange = (type: 'from' | 'to', value: Date | undefined) => {
        if (!value) return;
        if (!isValid(value)) return;
        if (type === 'from' && customRange?.dateFrom?.getTime() === value.getTime()) return;
        if (type === 'to' && customRange?.dateTo?.getTime() === value.getTime()) return;

        setCustomRange((prev) => {
            const newRange = {
                dateFrom: type === 'from' ? value : prev?.dateFrom || new Date(),
                dateTo: type === 'to' ? value : prev?.dateTo || new Date(),
            };
            if (isBefore(newRange.dateTo, newRange.dateFrom)) {
                return prev; // Prevent invalid range
            }
            return newRange;
        });
    };

    // Helper to get current historical options
    const getCurrentHistoricalOptions = () => {
        if (periodType === '1M') {
            return generateHistoricalPeriods(periodType, HISTORICAL_COUNTS.months);
        }
        if (periodType === '1Q') {
            return generateHistoricalPeriods(periodType, HISTORICAL_COUNTS.quarters);
        }
        if (periodType === '1Y') {
            return generateHistoricalPeriods(periodType, HISTORICAL_COUNTS.years);
        }
        return [];
    };

    const showRightMenu = ['1M', '1Q', '1Y'].includes(periodType);

    return (
        <Flex gap={2} flexWrap='wrap'>
            <Flex gap={0}>
                <Menu>
                    <MenuButton as={Button} py={1.5} rightIcon={<FontAwesomeIcon icon={faChevronDown} />} pl={4} pr={3} borderRightRadius={showRightMenu ? '0' : undefined} variant={showRightMenu ? "outline" : "solid"} whiteSpace="nowrap" h={8}>
                        {PERIOD_TYPES.find((opt) => opt.value === periodType)?.label || 'Select Period'}
                    </MenuButton>
                    <MenuList>
                        {PERIOD_TYPES.map((option) => (
                            <MenuItem key={option.value} onClick={() => setPeriodType(option.value)}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </MenuList>
                </Menu>

                {showRightMenu && (
                    <Menu>
                        <MenuButton as={Button} py={1.5} rightIcon={<FontAwesomeIcon icon={faChevronDown} />} pl={4} pr={3} borderLeftRadius={0} variant="solid" h={8}>
                            {getHistoricalLabel(periodType, historicalPeriod) || 'Vyberte období'}
                        </MenuButton>
                        <MenuList maxH="400px" overflowY="auto">
                            {getCurrentHistoricalOptions().map((option) => (
                                <MenuItem key={option.value} onClick={() => setHistoricalPeriod(option.value)}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </MenuList>
                    </Menu>
                )}
            </Flex>

            {/* Custom Range Inputs */}
            {periodType === 'CUSTOM' && (
                <Flex gap={2} minW='200px'>
                    <BaseDatepicker
                        size="sm"
                        date={customRange?.dateFrom}
                        onDateChange={(date) => handleCustomDateChange('from', date)}
                        options={{
                            maxDate: customRange?.dateTo
                        }}
                        placeholder="Start Date"
                        variant="outline"
                        sx={{
                            border: '1px solid', borderColor: 'primary.500', color: 'primary.600', _hover: { borderColor: 'primary.600', bg: 'gray.50' }
                        }}
                    />
                    <BaseDatepicker
                        size="sm"
                        date={customRange?.dateTo}
                        onDateChange={(date) => handleCustomDateChange('to', date)}
                        options={{
                            minDate: customRange?.dateFrom
                        }}
                        placeholder="End Date"
                        variant="outline"
                        sx={{
                            border: '1px solid', borderColor: 'primary.500', color: 'primary.600', _hover: { borderColor: 'primary.600', bg: 'gray.50' }
                        }}
                    />
                </Flex>
            )}
        </Flex>
    );
};
